import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Globe } from "lucide-react";
import { cn } from "@/lib/cn";
import { LANG_LABELS, SUPPORTED, type Lang } from "@/i18n";

// Full endonyms shown in the open menu (each in its own script).
const LANG_NAMES: Record<Lang, string> = { en: "English", si: "සිංහල", ta: "தமிழ்" };

// Language dropdown: a globe + current-language-code pill that opens a menu of the
// three supported languages. Each label renders in its own script so the switcher
// stays legible whatever the active language.
export function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const active = (SUPPORTED as readonly string[]).includes(i18n.language)
    ? (i18n.language as Lang)
    : "en";

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Language"
        className="flex items-center gap-1.5 rounded-full border border-edge-3 bg-surface px-3 py-2 text-[13px] font-semibold text-ink-2 hover:bg-surface-2"
      >
        <Globe size={15} className="text-muted" />
        {LANG_LABELS[active]}
        <ChevronDown
          size={14}
          className={cn("text-muted-2 transition-transform", open && "rotate-180")}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute right-0 top-full z-50 mt-1.5 min-w-40 overflow-hidden rounded-xl border border-edge-2 bg-surface p-1 shadow-lg"
        >
          {SUPPORTED.map((lng) => (
            <button
              key={lng}
              role="option"
              aria-selected={active === lng}
              onClick={() => {
                void i18n.changeLanguage(lng);
                setOpen(false);
              }}
              className={cn(
                "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[13px]",
                active === lng
                  ? "bg-surface-2 text-ink"
                  : "text-muted hover:bg-surface-2 hover:text-ink",
              )}
            >
              <span className="w-6 font-bold">{LANG_LABELS[lng]}</span>
              <span>{LANG_NAMES[lng]}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
